import { isEmpty, isNumber } from 'radash';
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useState } from 'react';

interface ViewInterface {
    name: string;
    fields: Array<string>;
}

interface CRUDViewContextInterface {
    onAddView: boolean;
    onEnterViewName: boolean;
    getAllViews: () => Array<ViewInterface>;
    selectedView: ViewInterface | undefined;
    setCurrentView: (index: number | undefined) => void;
    toggleAddView: () => void;
    addView: (isNewView?:boolean) => void;
    view: ViewInterface;
    setView: Dispatch<SetStateAction<ViewInterface>>;
    onCancel: () => void;
    onDeleteView: (index: number) => void;
    setOnEditView: (index: number) => void;
    onEditView: number | undefined;
    closeOtherPanels: boolean;
    setCloseOtherPanels: (value: boolean) => void;

}
const CRUDViewContext = createContext<CRUDViewContextInterface | undefined>(undefined);

const useCRUDView = () => {
    const viewContext = useContext(CRUDViewContext);

    if (!viewContext) {
        throw new Error('useCRUDView must be used within a CRUDViewProvider');
    }

    return viewContext;
};

const CRUDViewProvider = ({ children }: PropsWithChildren<Record<never, never>>) => {

    const newView = {
        name: '',
        fields: []
    };
    const currentView = localStorage.getItem('currentView');
    const parsedCurrentView = currentView ? JSON.parse(currentView) : null;

    const [onEnterViewName, setOnEnterViewName] = useState(false);
    const [selectedView, setSelectedView] = useState<ViewInterface| undefined>(parsedCurrentView);
    // Default to true if not set in sessionStorage, only false if explicitly set to 'false'
    const [closeOtherPanels, setCloseOtherPanels] = useState(() => {
        const storedValue = sessionStorage.getItem('closeOtherPanels');
        if (storedValue === null) {
            return true;
        }
        return storedValue !== 'false';
    });
    const [onAddView, setOnAddView] = useState(false);
    const [onEditView, _setOnEditView] = useState<number | undefined>(undefined);

    const [view, setView] = useState<ViewInterface>(newView);

    const setCurrentView = (viewIndex: number | undefined) => {
        if(isNumber(viewIndex)) {
            const selectedView: ViewInterface = getAllViews()[viewIndex];
            localStorage.setItem('currentView', JSON.stringify(selectedView));
            setSelectedView(selectedView);
        } else {
            localStorage.removeItem('currentView');
            setSelectedView(undefined);
        }
    };

    const getAllViews = () => {
        const viewsList = localStorage.getItem('userviews');
        const parsedViewsList = viewsList ? JSON.parse(viewsList) : null;
        return parsedViewsList ?? [];
    };

    const addView = (isNewView?:boolean) => {
        if (isNewView) {
            setView(prevState => {
                prevState.name = '';
                return prevState;
            });
        }

        if (isNumber(onEditView) && (!isNewView)) {
            const views = getAllViews();
            views[onEditView] = view;
            localStorage.setItem('userviews', JSON.stringify(views));
            setOnAddView(false);
            setOnEnterViewName(false);
            _setOnEditView(undefined);
            setView(newView);
        } else if (isEmpty(view.name)) {
            setOnEnterViewName(true);
        } else {
            const viewsList: Array<ViewInterface> = getAllViews();
            localStorage.setItem('userviews', JSON.stringify([...viewsList, view]));
            setOnAddView(false);
            setOnEnterViewName(false);
            setView(newView);
        }
    };

    const onCancel = () => {
        setOnEnterViewName(false);
        setOnAddView(false);
        _setOnEditView(undefined);
    };

    const toggleAddView = () => {
        setOnAddView(prevState => !prevState);
    };

    const setOnEditView = (index: number) => {
        setCurrentView(undefined);
        const views = getAllViews();
        setView(views[index]);
        _setOnEditView((prevState) => {
            if (prevState === index ) {
                return undefined;
            }
            return index;
        });
    };

    const onDeleteView =(index:number)=>{
        onCancel();
        let views = getAllViews();
        views = views.filter((_: ViewInterface, i: number) => i !== index);
        localStorage.setItem('userviews', JSON.stringify(views));
    };

    return (
        <CRUDViewContext.Provider value={{ onAddView, onEnterViewName, toggleAddView, getAllViews, addView,
            selectedView, setCurrentView, view, setView, onCancel, onEditView,  setOnEditView , onDeleteView, closeOtherPanels, setCloseOtherPanels}}>
            {children}
        </CRUDViewContext.Provider>
    );
};

export { CRUDViewProvider, useCRUDView };
